import type { ConfiguratorDefinition } from "../../common/contract/definition";
import { BALL_SCREW_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { BALL_SCREW_CSTICS, BALL_SCREW_DERIVED_CSTICS, BALL_SCREW_SYNTHETIC_CSTICS } from "./ballScrewCstics";

export const BALL_SCREW_CONFIGURATOR_DEFINITION = {
  productCode: BALL_SCREW_CONFIGURATOR_PRODUCT_CODE,
  titleKey: "configurator.screw.name",
  stepResetScope: "stepAndSubsequent",
  steps: [
    {
      id: "basicConfiguration",
      titleKey: "configurator.screw.step.1.name",
      substeps: [
        {
          controls: [
            {
              type: "TileSelect",
              cstic: BALL_SCREW_CSTICS.shaftOrAssembly,
              tileLayout: "fill",
            },
          ],
        },
        {
          controlsLayout: { type: "grid", columns: 2 },
          controls: [
            {
              type: "DropdownSelect",
              cstic: BALL_SCREW_CSTICS.shaftDiameter,
              hideDisabled: true,
              placeholderKey: "configurator.screw.common.select",
              infoPresentation: "richTooltip",
            },
            {
              type: "DropdownSelect",
              cstic: BALL_SCREW_CSTICS.lead,
              hideDisabled: true,
              placeholderKey: "configurator.screw.common.select",
              infoPresentation: "richTooltip",
            },
            {
              type: "NumberInput",
              cstic: BALL_SCREW_CSTICS.threadLength,
              placeholderKey: "configurator.screw.cstic.thread.placeholder",
              helperTextCstic: BALL_SCREW_DERIVED_CSTICS.threadLengthHelperText,
              infoPresentation: "richTooltip",
            },
          ],
        },
        {
          controls: [
            {
              type: "TileSelect",
              cstic: BALL_SCREW_CSTICS.leadPrecision,
              tileLayout: "compact",
            },
          ],
        },
        {
          controls: [
            {
              type: "TileSelect",
              cstic: BALL_SCREW_CSTICS.material,
              tileLayout: "compact",
            },
          ],
        },
      ],
    },
    {
      id: "nut",
      titleKey: "configurator.screw.step.2.name",
      disabledWhen: {
        cstic: { name: BALL_SCREW_CSTICS.shaftOrAssembly },
        valueIn: ["N"],
      },
      substeps: [
        {
          controlsLayout: { type: "flow" },
          controls: [
            {
              type: "SelectChip",
              cstic: BALL_SCREW_CSTICS.axialPlayOrPreload,
              summary: { miniSummary: false },
            },
            {
              type: "SelectChip",
              cstic: BALL_SCREW_CSTICS.nutFamily,
              summary: { miniSummary: false },
            },
            {
              type: "SelectChip",
              cstic: BALL_SCREW_CSTICS.nutVariant,
              summary: { miniSummary: false },
            },
            {
              type: "SelectChip",
              cstic: BALL_SCREW_CSTICS.dustAndDirtProtection,
              summary: { miniSummary: false },
            },
            {
              type: "SelectChip",
              cstic: BALL_SCREW_CSTICS.safetyRing,
              summary: { miniSummary: false },
            },
            {
              type: "SelectChip",
              cstic: BALL_SCREW_CSTICS.recirculationInserts,
              summary: { miniSummary: false },
            },
          ],
        },
        {
          controls: [
            {
              type: "TileSelect",
              cstic: BALL_SCREW_CSTICS.nutType,
              tileLayout: "compact",
              sortSelectableFirst: true,
            },
          ],
        },
        {
          controls: [
            {
              type: "TileSelect",
              cstic: BALL_SCREW_CSTICS.mountingFlange,
              hideDisabled: true,
              tileLayout: "compact",
              visibleWhen: {
                cstic: { name: BALL_SCREW_CSTICS.nutType },
                valueIn: ["SX", "BX"],
              },
            },
          ],
        },
      ],
    },
    {
      id: "bearings",
      titleKey: "configurator.screw.step.3.name",
      unlockedWhen: {
        allStepsSatisfied: ["basicConfiguration", "nut"],
      },
      visibleWhen: {
        cstic: { name: BALL_SCREW_CSTICS.bearingAvailability },
        valueIn: ["J"],
      },
      substeps: [
        {
          renderer: "carouselColumns",
          columns: [
            {
              carousel: {
                type: "CarouselSelect",
                cstic: BALL_SCREW_CSTICS.leftBearingType,
                hideDisabled: true,
                showPreviewDescription: true,
              },
              details: [
                {
                  type: "DropdownSelect",
                  cstic: BALL_SCREW_CSTICS.leftLockingNut,
                  placeholderKey: "configurator.screw.common.select",
                  visibleWhen: {
                    cstic: { name: BALL_SCREW_CSTICS.leftBearingType },
                    valueIn: ["FLBU", "PLBU"],
                  },
                },
              ],
            },
            {
              carousel: {
                type: "CarouselSelect",
                cstic: BALL_SCREW_CSTICS.rightBearingType,
                hideDisabled: true,
                showPreviewDescription: true,
              },
              details: [
                {
                  type: "DropdownSelect",
                  cstic: BALL_SCREW_CSTICS.rightLockingNut,
                  placeholderKey: "configurator.screw.common.select",
                  visibleWhen: {
                    cstic: { name: BALL_SCREW_CSTICS.rightBearingType },
                    valueIn: ["FLBU", "PLBU"],
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "endMachining",
      titleKey: "configurator.screw.step.4.name",
      unlockedWhen: {
        allStepsSatisfied: ["bearings"],
      },
      substeps: [
        {
          controls: [
            {
              type: "TabSelect",
              cstic: BALL_SCREW_SYNTHETIC_CSTICS.endMachiningMode,
              hideLabel: true,
              summary: { include: false },
            },
          ],
        },
        {
          renderer: "carouselColumns",
          visibleWhen: {
            cstic: { name: BALL_SCREW_SYNTHETIC_CSTICS.endMachiningMode },
            valueIn: ["catalogue"],
          },
          columns: [
            {
              carousel: {
                type: "CarouselSelect",
                cstic: BALL_SCREW_CSTICS.leftEndMachining,
                hideDisabled: true,
                hiddenValues: ["Z"],
                infoPresentation: "richTooltip",
              },
              details: [
                {
                  type: "NumberInput",
                  cstic: BALL_SCREW_CSTICS.leftMachinedLength,
                  placeholderKey: "configurator.screw.cstic.thread.placeholder",
                  visibleWhen: {
                    cstic: { name: BALL_SCREW_CSTICS.leftEndMachining },
                    valueIn: ["A", "AA", "S", "SA", "AS", "UA", "AU"],
                  },
                },
                {
                  type: "Checkbox",
                  cstic: BALL_SCREW_CSTICS.leftKeyway,
                  checkedValue: "J",
                  uncheckedValue: "N",
                  visibleWhen: {
                    all: [
                      {
                        cstic: { name: BALL_SCREW_CSTICS.leftEndMachining },
                        valueIn: ["2", "2A", "5", "5A"],
                      },
                      {
                        cstic: { name: BALL_SCREW_CSTICS.shaftDiameter },
                        numeric: { operator: "gte", value: 12 },
                      },
                    ],
                  },
                },
              ],
            },
            {
              carousel: {
                type: "CarouselSelect",
                cstic: BALL_SCREW_CSTICS.rightEndMachining,
                hideDisabled: true,
                hiddenValues: ["Z"],
                infoPresentation: "richTooltip",
              },
              details: [
                {
                  type: "NumberInput",
                  cstic: BALL_SCREW_CSTICS.rightMachinedLength,
                  placeholderKey: "configurator.screw.cstic.thread.placeholder",
                  visibleWhen: {
                    cstic: { name: BALL_SCREW_CSTICS.rightEndMachining },
                    valueIn: ["A", "AA", "S", "SA", "AS", "UA", "AU"],
                  },
                },
                {
                  type: "Checkbox",
                  cstic: BALL_SCREW_CSTICS.rightKeyway,
                  checkedValue: "J",
                  uncheckedValue: "N",
                  visibleWhen: {
                    all: [
                      {
                        cstic: { name: BALL_SCREW_CSTICS.rightEndMachining },
                        valueIn: ["2", "2A", "5", "5A"],
                      },
                      {
                        cstic: { name: BALL_SCREW_CSTICS.shaftDiameter },
                        numeric: { operator: "gte", value: 12 },
                      },
                    ],
                  },
                },
              ],
            },
          ],
        },
        {
          renderer: "configurationAttachment",
          required: true,
          titleKey: "configurator.screw.custom.attachment.title",
          formatTextKey: "configurator.screw.cstic.customEndMachining.upload.fileSizeLimit.description",
          visibleWhen: {
            cstic: { name: BALL_SCREW_SYNTHETIC_CSTICS.endMachiningMode },
            valueIn: ["custom"],
          },
        },
        {
          controlsLayout: { type: "grid", columns: 2 },
          visibleWhen: {
            cstic: { name: BALL_SCREW_SYNTHETIC_CSTICS.endMachiningMode },
            valueIn: ["custom"],
          },
          controls: [
            {
              type: "StaticImageSelect",
              cstic: BALL_SCREW_CSTICS.customCylindricalParts,
              forceOptionsEnabled: true,
              hideLabel: true,
              placeholderKey: "configurator.screw.common.select",
            },
            {
              type: "StaticImageSelect",
              cstic: BALL_SCREW_CSTICS.customIsoThreads,
              forceOptionsEnabled: true,
              hideLabel: true,
              placeholderKey: "configurator.screw.common.select",
            },
            {
              type: "StaticImageSelect",
              cstic: BALL_SCREW_CSTICS.customMilledParts,
              forceOptionsEnabled: true,
              hideLabel: true,
              placeholderKey: "configurator.screw.common.select",
            },
            {
              type: "StaticImageSelect",
              cstic: BALL_SCREW_CSTICS.customAxialHoles,
              forceOptionsEnabled: true,
              hideLabel: true,
              placeholderKey: "configurator.screw.common.select",
            },
          ],
        },
        {
          visibleWhen: {
            all: [
              {
                cstic: { name: BALL_SCREW_CSTICS.shaftOrAssembly },
                valueIn: ["J"],
              },
              {
                cstic: { name: BALL_SCREW_SYNTHETIC_CSTICS.endMachiningMode },
                valueIn: ["catalogue"],
              },
            ],
          },
          controls: [
            {
              type: "TileSelect",
              cstic: BALL_SCREW_CSTICS.nutOrientation,
              tileLayout: "compact",
              required: true,
            },
          ],
        },
      ],
    },
  ],
} satisfies ConfiguratorDefinition;
